import type { ReplacementQuote } from '@/lib/backupReplacementApi'
import type { PackageSelection } from '@/lib/registrationIntent'
import {
  createPolicyConsentIdentity,
  NON_PAYMENT_POLICY_CONTENT_HASH,
  NON_PAYMENT_POLICY_VERSION,
  TERMS_CONTENT_HASH,
  TERMS_VERSION,
} from '@/lib/policyConsent'

type SavedRequest = { key: string; body: Record<string, unknown>; quotedAmountMinor: number; currency: string }
export type CommittedReplacement = SavedRequest & { packageVersionId: number; locationId: number }

function storage(): Storage | null {
  try { return typeof window === 'undefined' ? null : window.sessionStorage } catch { return null }
}

function requestStorageKey(ownerUuid: string, backupUuid: string, packageVersionId: number, locationId: number): string {
  return `peltast:vault-replacement:${ownerUuid}:${backupUuid}:${packageVersionId}:${locationId}`
}

function committedStorageKey(ownerUuid: string, backupUuid: string): string {
  return `peltast:vault-replacement:${ownerUuid}:${backupUuid}:committed`
}

export function committedReplacement(ownerUuid: string, backupUuid: string): CommittedReplacement | null {
  try {
    const value = JSON.parse(storage()?.getItem(committedStorageKey(ownerUuid, backupUuid)) || 'null') as CommittedReplacement | null
    return value?.key && value.body && Number.isSafeInteger(value.packageVersionId) && Number.isSafeInteger(value.locationId) ? value : null
  } catch { return null }
}

export function replacementRestoreRetryKey(ownerUuid: string, backupUuid: string, intentUuid: string, failedOperationId: string): string {
  const store = storage()
  if (!store) throw new Error('This browser cannot safely resume a restore retry. Enable session storage and try again.')
  const path = `peltast:vault-restore-retry:${ownerUuid}:${backupUuid}:${intentUuid}:${failedOperationId}`
  const prior = store.getItem(path)
  if (prior) return prior
  const key = newKey()
  store.setItem(path, key)
  return key
}

function newKey(): string {
  return typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function'
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
}

export function replacementRequestKey(ownerUuid: string, backupUuid: string, packageVersionId: number, locationId: number): string {
  const committed = committedReplacement(ownerUuid, backupUuid)
  if (committed) {
    if (committed.packageVersionId !== packageVersionId || committed.locationId !== locationId) {
      throw new Error('This backup already has a purchase request. Review its existing status before choosing another package.')
    }
    return committed.key
  }
  const store = storage()
  if (!store) throw new Error('This browser cannot safely resume a purchase request. Enable session storage and try again.')
  const key = requestStorageKey(ownerUuid, backupUuid, packageVersionId, locationId)
  try {
    const previous = JSON.parse(store.getItem(key) || 'null') as SavedRequest | null
    if (previous?.key) return previous.key
  } catch { /* a malformed local entry cannot authorize a purchase */ }
  const requestKey = newKey()
  store.setItem(key, JSON.stringify({ key: requestKey }))
  return requestKey
}

export function savedReplacementRequest(ownerUuid: string, backupUuid: string, packageVersionId: number, locationId: number): SavedRequest | null {
  try {
    const saved = JSON.parse(storage()?.getItem(requestStorageKey(ownerUuid, backupUuid, packageVersionId, locationId)) || 'null') as SavedRequest | null
    return saved?.key && saved?.body ? saved : null
  } catch { return null }
}

export function saveReplacementRequest(ownerUuid: string, backupUuid: string, packageVersionId: number, locationId: number, key: string, body: Record<string, unknown>, quotedAmountMinor: number, currency: string): void {
  const store = storage()
  if (!store) throw new Error('This browser cannot safely resume a purchase request. Enable session storage and try again.')
  const path = requestStorageKey(ownerUuid, backupUuid, packageVersionId, locationId)
  const prior = savedReplacementRequest(ownerUuid, backupUuid, packageVersionId, locationId)
  const committed = committedReplacement(ownerUuid, backupUuid)
  if (committed && (committed.packageVersionId !== packageVersionId || committed.locationId !== locationId
    || committed.key !== key || JSON.stringify(committed.body) !== JSON.stringify(body)
    || committed.quotedAmountMinor !== quotedAmountMinor || committed.currency !== currency)) {
    throw new Error('This backup already has a purchase request. Review its existing status before choosing another package.')
  }
  if (prior && (prior.key !== key || JSON.stringify(prior.body) !== JSON.stringify(body)
    || prior.quotedAmountMinor !== quotedAmountMinor || prior.currency !== currency)) {
    throw new Error('An earlier purchase request needs status review before its terms or settings can change.')
  }
  store.setItem(path, JSON.stringify({ key, body, quotedAmountMinor, currency }))
  store.setItem(committedStorageKey(ownerUuid, backupUuid), JSON.stringify({ key, body, quotedAmountMinor, currency, packageVersionId, locationId }))
}

export function assertReplacementQuote(quote: ReplacementQuote, selected: PackageSelection, backupUuid: string, sourceServerUuid: string, eggId: number, locationId: number): void {
  if (quote.source_backup_uuid !== backupUuid || quote.source_server_uuid !== sourceServerUuid
    || quote.egg_id !== eggId || quote.location_id !== locationId
    || quote.pricing_basis !== 'new_purchase' || quote.terms.version_id !== selected.package_version_id
    || quote.terms.minimum_minor !== selected.pricing.minimum_minor
    || quote.terms.ceiling_minor !== selected.pricing.ceiling_minor
    || quote.terms.currency !== selected.currency
    || quote.currency !== selected.currency || !Number.isSafeInteger(quote.initial_minimum_minor)
    || quote.initial_minimum_minor <= 0
    || !Number.isFinite(Date.parse(quote.expires_at)) || Date.parse(quote.expires_at) <= Date.now()) {
    throw new Error('The current replacement quote changed or expired. Review the package again.')
  }
}

export function buildReplacementPolicyAcceptance(quote: ReplacementQuote): Record<string, unknown> {
  const identity = createPolicyConsentIdentity()
  const disclosures = quote.public_terms.recovery && quote.public_terms.backups
    ? { recovery: quote.public_terms.recovery, backups: quote.public_terms.backups }
    : undefined
  return {
    consent_bundle_uuid: identity.consentBundleUuid,
    registration_record_uuid: identity.registrationRecordUuid,
    terms_accepted: true,
    non_payment_policy_acknowledged: true,
    recurring_billing_authorized: true,
    new_purchase_authorized: true,
    new_purchase_authorization_text: quote.new_purchase_authorization_text,
    recurring_authorization_text: quote.recurring_authorization_text,
    terms_version: TERMS_VERSION,
    terms_content_hash: TERMS_CONTENT_HASH,
    non_payment_policy_version: NON_PAYMENT_POLICY_VERSION,
    non_payment_policy_content_hash: NON_PAYMENT_POLICY_CONTENT_HASH,
    package_quote_hash: quote.quote_hash,
    displayed_initial_minimum_minor: quote.initial_minimum_minor,
    displayed_initial_minimum_currency: quote.currency,
    displayed_package: {
      package_version_id: quote.terms.version_id,
      currency: quote.terms.currency,
      minimum_minor: quote.terms.minimum_minor,
      ceiling_minor: quote.terms.ceiling_minor,
      ...(disclosures ? { disclosures } : {}),
    },
    customer_locale: typeof navigator === 'undefined' ? 'en-US' : navigator.language,
    currency: quote.terms.currency,
    displayed_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
  }
}
